/**
 * RDStationConfig Component
 *
 * Configuration for RD Station CRM integration:
 * - Toggle to enable automatic deal creation
 * - Dropdown for deal stage selection
 * - Dropdown for deal source selection
 * - Deal name template input
 * - Custom field mapping (key-value pairs)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Form } from 'react-bootstrap';
import Select from 'react-select';
import {
  IconChartBar,
  IconRefresh,
  IconAlertCircle,
  IconPlus,
  IconX,
} from '@tabler/icons-react';
import { api } from '../../../../api/Middleware';
import { API_URL } from '../../../../api/Urls';

const RDStationConfig = ({ agentId, integrationId, config, onConfigChange }) => {
  const [loading, setLoading] = useState(true);
  const [dealStages, setDealStages] = useState([]);
  const [dealSources, setDealSources] = useState([]);
  const [error, setError] = useState(null);

  // Current config values
  const createDeal = config?.rdstation_create_deal || false;
  const selectedStageId = config?.rdstation_deal_stage_id || '';
  const selectedSourceId = config?.rdstation_deal_source_id || '';
  const dealName = config?.rdstation_deal_name || 'Lead: {name}';
  const fieldMapping = config?.rdstation_field_mapping || {};

  // Fetch deal stages and sources from API
  const fetchDealData = useCallback(async () => {
    if (!agentId || !integrationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `${API_URL}agents_flow/agents/${agentId}/integrations/${integrationId}/rdstation-deal-stages/`
      );
      setDealStages(response.data.deal_stages || []);
      setDealSources(response.data.deal_sources || []);
    } catch (err) {
      console.error('Error fetching RD Station data:', err);
      const msg = err.response?.data?.error || 'Erro ao carregar dados do RD Station';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [agentId, integrationId]);

  useEffect(() => {
    fetchDealData();
  }, [fetchDealData]);

  // Update config helper
  const updateConfig = (updates) => {
    onConfigChange({
      rdstation_create_deal: createDeal,
      rdstation_deal_stage_id: selectedStageId,
      rdstation_deal_source_id: selectedSourceId,
      rdstation_deal_name: dealName,
      rdstation_field_mapping: fieldMapping,
      ...updates,
    });
  };

  // Build react-select options
  const stageOptions = dealStages.map((s) => ({
    value: s._id,
    label: s.name,
  }));

  const sourceOptions = dealSources.map((s) => ({
    value: s._id,
    label: s.name,
  }));

  const selectedStageOption = stageOptions.find((o) => o.value === selectedStageId) || null;
  const selectedSourceOption = sourceOptions.find((o) => o.value === selectedSourceId) || null;

  // Field mapping handlers
  const fieldMappingEntries = Object.entries(fieldMapping);

  const handleAddMapping = () => {
    const newMapping = { ...fieldMapping, '': '' };
    updateConfig({ rdstation_field_mapping: newMapping });
  };

  const handleRemoveMapping = (key) => {
    const newMapping = { ...fieldMapping };
    delete newMapping[key];
    updateConfig({ rdstation_field_mapping: newMapping });
  };

  const handleMappingKeyChange = (oldKey, newKey) => {
    const newMapping = {};
    for (const [k, v] of Object.entries(fieldMapping)) {
      if (k === oldKey) {
        newMapping[newKey] = v;
      } else {
        newMapping[k] = v;
      }
    }
    updateConfig({ rdstation_field_mapping: newMapping });
  };

  const handleMappingValueChange = (key, newValue) => {
    updateConfig({
      rdstation_field_mapping: { ...fieldMapping, [key]: newValue },
    });
  };

  // Custom styles for react-select (amber/orange theme)
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#d97706' : '#dee2e6',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(217, 119, 6, 0.15)' : 'none',
      '&:hover': { borderColor: '#d97706' },
      minHeight: '42px',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#fef3c7' : state.isFocused ? '#f5f5f5' : 'white',
      color: '#333',
      padding: '8px 12px',
      '&:active': { backgroundColor: '#fde68a' },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 10,
    }),
    placeholder: (base) => ({
      ...base,
      color: '#999',
    }),
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spinner animation="border" size="sm" style={{ color: '#d97706' }} />
        <span style={{ marginLeft: '8px', color: '#666' }}>Carregando dados do RD Station...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px', background: '#fff3f3', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <IconAlertCircle size={18} style={{ color: '#dc3545' }} />
        <span style={{ color: '#dc3545', fontSize: '14px', flex: 1 }}>{error}</span>
        <button
          onClick={fetchDealData}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d97706', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}
        >
          <IconRefresh size={14} /> Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Toggle: criar negócio automaticamente */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <label style={{ fontWeight: 500, fontSize: '13px', color: '#444', display: 'block' }}>
            Criar negócio automaticamente
          </label>
          <span style={{ fontSize: '11px', color: '#999' }}>
            Cria um negócio no RD Station CRM ao coletar dados do contato
          </span>
        </div>
        <Form.Check
          type="switch"
          checked={createDeal}
          onChange={(e) => updateConfig({ rdstation_create_deal: e.target.checked })}
          style={{ transform: 'scale(1.2)' }}
        />
      </div>

      {/* Conditional section: deal configuration */}
      {createDeal && (
        <>
          {/* Deal stage dropdown */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ fontWeight: 500, fontSize: '13px', color: '#444' }}>
                Estágio do negócio
              </label>
              <button
                onClick={fetchDealData}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}
                title="Recarregar lista"
              >
                <IconRefresh size={13} /> Atualizar
              </button>
            </div>
            <Select
              options={stageOptions}
              value={selectedStageOption}
              onChange={(option) => updateConfig({ rdstation_deal_stage_id: option?.value || '' })}
              styles={selectStyles}
              placeholder="Selecione o estágio..."
              noOptionsMessage={() => 'Nenhum estágio encontrado'}
              isClearable
              isSearchable
              maxMenuHeight={200}
            />
            <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
              {dealStages.length} estágio{dealStages.length !== 1 ? 's' : ''} encontrado{dealStages.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Deal source dropdown */}
          <div>
            <label style={{ fontWeight: 500, fontSize: '13px', color: '#444', marginBottom: '6px', display: 'block' }}>
              Fonte do negócio
            </label>
            <Select
              options={sourceOptions}
              value={selectedSourceOption}
              onChange={(option) => updateConfig({ rdstation_deal_source_id: option?.value || '' })}
              styles={selectStyles}
              placeholder="Selecione a fonte..."
              noOptionsMessage={() => 'Nenhuma fonte encontrada'}
              isClearable
              isSearchable
              maxMenuHeight={200}
            />
            <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
              {dealSources.length} fonte{dealSources.length !== 1 ? 's' : ''} encontrada{dealSources.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Deal name template */}
          <div>
            <label style={{ fontWeight: 500, fontSize: '13px', color: '#444', marginBottom: '6px', display: 'block' }}>
              Nome do negócio
            </label>
            <input
              type="text"
              value={dealName}
              onChange={(e) => updateConfig({ rdstation_deal_name: e.target.value })}
              placeholder="Lead: {name}"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
            <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
              Use {'{name}'}, {'{phone}'} ou {'{email}'} como variáveis
            </div>
          </div>
        </>
      )}

      {/* Field mapping section */}
      <div style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
        <label style={{ fontWeight: 500, fontSize: '13px', color: '#444', display: 'block', marginBottom: '4px' }}>
          Mapeamento de campos
        </label>
        <span style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: '12px' }}>
          Mapeie campos coletados para campos customizados do RD Station. Se não mapeado, o nome do campo será usado como custom_field_id.
        </span>

        {fieldMappingEntries.map(([key, value], index) => (
          <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
            <input
              type="text"
              value={key}
              onChange={(e) => handleMappingKeyChange(key, e.target.value)}
              placeholder="Campo coletado"
              style={{
                flex: 1,
                padding: '6px 10px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                fontSize: '13px',
                outline: 'none',
              }}
            />
            <span style={{ color: '#999', fontSize: '13px' }}>→</span>
            <input
              type="text"
              value={value}
              onChange={(e) => handleMappingValueChange(key, e.target.value)}
              placeholder="custom_field_id no RD Station"
              style={{
                flex: 1,
                padding: '6px 10px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                fontSize: '13px',
                outline: 'none',
              }}
            />
            <button
              onClick={() => handleRemoveMapping(key)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#dc3545',
                display: 'flex',
                alignItems: 'center',
                padding: '4px',
              }}
              title="Remover mapeamento"
            >
              <IconX size={16} />
            </button>
          </div>
        ))}

        <button
          onClick={handleAddMapping}
          style={{
            background: 'none',
            border: '1px dashed #d97706',
            borderRadius: '6px',
            cursor: 'pointer',
            color: '#d97706',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '13px',
            padding: '6px 12px',
            width: '100%',
            justifyContent: 'center',
          }}
        >
          <IconPlus size={14} /> Adicionar mapeamento
        </button>
      </div>
    </div>
  );
};

export default RDStationConfig;
